import React, { FC, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectIsGAInitialized } from "../../store/selectors/appSelectors";
import Footer from "../Footer/Footer";
import { AddGoogleAnalyticsPageView } from "../GoogleAnalyticsHandler/helpers";
import Header from "../Header/Header";
import MobileCampaignStrip from "../MobileCampaignStrip";

import styles from "./pageWrapper.module.scss";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import { UserRoleE } from "../../models/enums";
import { RoutesE } from "../../routes/routesData";
import { useLazyGetServicePackagesForClientQuery } from "../../api/servicePackagesAPI";
import { setServicePackages } from "../../store/slices/servicePackageSlice";
import useContactInfoValidationGate from "../../utils/hooks/useContactInfoValidationGate";

// Public marketing pages (no logged-in user) that keep the original dark, sticky footer and the
// original flex content layout — never the authenticated grid layout or light footer.
const MARKETING_ROUTES: string[] = [
  RoutesE.HOME,
  RoutesE.ABOUT,
  RoutesE.FEATURES,
  RoutesE.PROCESS,
  RoutesE.CONTACT_US,
];

/**
 * Page wrapper includes Header component
 * */

const PageWrapper: FC<{ children: any }> = ({ children }) => {
  const location = useLocation();
  const isGoogleAnalyticsInitialized = useAppSelector(selectIsGAInitialized);
  const user = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();
  const [triggerGetServicePackagesForClient] = useLazyGetServicePackagesForClientQuery();
  const [showContentOnly, setShowContentOnly] = useState(false);

  // Single mount point for the mandatory "Personal contact info" popup (previously triggered from
  // LangSelect, which is mounted in several places at once — the cause of the duplicate popup).
  useContactInfoValidationGate();

  useEffect(() => {
    if (user?.role === UserRoleE.GlobalAdmin) {
      triggerGetServicePackagesForClient({}).then(({ data }) => {
        data && dispatch(setServicePackages(data?.reduce((acc, item) => {
          acc[item.ServicePackageId] = item.ServicePackageName;
          return acc;
        }, {}) || {}));
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    if (location.pathname.includes("/account/registration/")) {
      setShowContentOnly(true);
    } else {
      setShowContentOnly(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    AddGoogleAnalyticsPageView({ isGoogleAnalyticsInitialized });
  }, [isGoogleAnalyticsInitialized, location.pathname]);

  const isMarketingPage =
    !user && MARKETING_ROUTES.includes(location.pathname);

  if (isMarketingPage) {
    // Public marketing pages: original flex content layout + dark sticky footer rendered as a
    // sibling pinned to the viewport bottom (the pages reserve 50px for it themselves).
    return (
      <div className={styles.container}>
        {!showContentOnly && <MobileCampaignStrip />}
        {!showContentOnly && <Header />}
        <div id="app-scroll" className={styles.pageContentMarketing}>
          {children}
        </div>
        <Footer variant="dark" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {!showContentOnly && <MobileCampaignStrip />}
      {!showContentOnly && <Header />}
      <div
        id="app-scroll"
        className={showContentOnly ? styles.pageContentOnly : styles.pageContent}
      >
        {children}
        {/* Authenticated app pages get the shared light footer in the content column. */}
        {!showContentOnly && user && <Footer />}
      </div>
    </div>
  );
};

export default PageWrapper;
